import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { uploadFileJob, uploadTextJob, extractJob } from "../../api/ingest.api";

// Per-source max file size. The Django side will also enforce limits;
// these match what's reasonable to upload + extract within Lambda /
// gunicorn timeouts. Bumping is fine but should be coordinated with
// the backend ingest config.
const MAX_BYTES = 50 * 1024 * 1024; // 50 MB

const KINDS = [
  { value: "PLAYER_PROFILE", label: "Player profile xlsx (single .xlsx or .zip of bulk folder)", file: true,  ai: false },
  { value: "PDF",            label: "PDF scorecard(s)",                                          file: true,  ai: true  },
  { value: "IMAGE",          label: "Screenshot / image of a scorecard",                        file: true,  ai: true  },
  { value: "EXCEL",          label: "Excel / CSV with match data",                              file: true,  ai: true  },
  { value: "XML",            label: "XML feed",                                                  file: true,  ai: true  },
  { value: "TEXT",           label: "Pasted text",                                               file: false, ai: true  },
];

export default function NewIngestJob() {
  const navigate = useNavigate();
  const [kind, setKind] = useState("PLAYER_PROFILE");
  const [file, setFile] = useState(null);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [step, setStep] = useState("idle"); // idle / uploading / extracting / done
  const [error, setError] = useState("");

  const cur = KINDS.find((k) => k.value === kind);

  const submit = async () => {
    setError("");
    if (cur.file && !file) { setError("Pick a file first"); return; }
    if (cur.file && file && file.size > MAX_BYTES) {
      setError(
        `File is too large (${(file.size / 1024 / 1024).toFixed(1)} MB). ` +
        `Max is ${MAX_BYTES / 1024 / 1024} MB — split the source into smaller files.`,
      );
      return;
    }
    if (!cur.file && !text.trim()) { setError("Paste some text first"); return; }
    setBusy(true);
    try {
      setStep("uploading");
      const job = cur.file
        ? await uploadFileJob(kind, file)
        : await uploadTextJob(text, "pasted.txt");
      setStep("extracting");
      const after = await extractJob(job.id);
      setStep("done");
      navigate(`/ingest/jobs/${after.id || job.id}`);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "Upload failed");
      setStep("idle");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-bg-main py-12 px-4">
      <div className="max-w-3xl mx-auto">
        <Link to="/ingest" className="text-text-secondary text-sm hover:text-text-primary">
          ← Back to jobs
        </Link>
        <h1 className="text-3xl font-bold text-text-primary mt-3 mb-1">New upload</h1>
        <p className="text-text-secondary text-sm mb-8">
          We'll extract data from your source, show it for review, then commit on your approval.
        </p>

        <div className="bg-bg-primary border border-bg-secondary rounded-xl p-6 space-y-6">
          <div>
            <label className="block text-text-primary font-medium mb-2">Source kind</label>
            <select
              value={kind}
              onChange={(e) => { setKind(e.target.value); setFile(null); setText(""); }}
              className="w-full bg-bg-secondary text-text-primary border border-bg-secondary rounded-lg px-3 py-2"
              disabled={busy}
            >
              {KINDS.map((k) => (
                <option key={k.value} value={k.value}>{k.label}</option>
              ))}
            </select>
            <p className="text-xs text-text-secondary mt-2">
              {cur.ai
                ? "Uses Claude (claude-sonnet-4-6) for extraction. Cost is logged on the job."
                : "Deterministic xlsx parser — no AI, $0 cost."}
            </p>
          </div>

          {cur.file ? (
            <div>
              <label className="block text-text-primary font-medium mb-2">File</label>
              <input
                type="file"
                accept={
                  kind === "PLAYER_PROFILE" ? ".xlsx,.zip"
                  : kind === "PDF"          ? ".pdf"
                  : kind === "IMAGE"        ? ".png,.jpg,.jpeg,.webp"
                  : kind === "EXCEL"        ? ".xlsx,.xls,.csv"
                  : kind === "XML"          ? ".xml"
                  : "*"
                }
                onChange={(e) => setFile(e.target.files[0] || null)}
                disabled={busy}
                className="block w-full text-sm text-text-primary
                  file:mr-4 file:py-2 file:px-4 file:rounded file:border-0
                  file:bg-button-secondary file:text-text-primary
                  hover:file:bg-button-primary file:cursor-pointer"
              />
              {file && (
                <p className={`text-xs mt-2 ${file.size > MAX_BYTES ? "text-rose-300" : "text-text-secondary"}`}>
                  {file.name} — {(file.size / 1024 / 1024).toFixed(2)} MB
                  {file.size > MAX_BYTES && (
                    <> · over the {MAX_BYTES / 1024 / 1024} MB limit</>
                  )}
                </p>
              )}
              <p className="text-[11px] text-text-secondary mt-1">
                Max {MAX_BYTES / 1024 / 1024} MB per upload. Extraction
                typically takes 10–60 seconds depending on source size and
                whether AI is involved.
              </p>
            </div>
          ) : (
            <div>
              <label className="block text-text-primary font-medium mb-2">Pasted text</label>
              <textarea
                rows={10}
                value={text}
                onChange={(e) => setText(e.target.value)}
                disabled={busy}
                placeholder="Paste your scorecard text here…"
                className="w-full bg-bg-secondary text-text-primary border border-bg-secondary rounded-lg px-3 py-2 font-mono text-sm"
              />
            </div>
          )}

          {error && (
            <div className="bg-rose-500/10 border border-rose-500/30 text-rose-300 rounded-lg p-3 text-sm">
              {error}
            </div>
          )}

          <div className="flex items-center gap-3">
            <button
              onClick={submit}
              disabled={busy}
              className="bg-emerald-600 hover:bg-emerald-700 disabled:bg-emerald-600/30 text-white px-5 py-2 rounded-lg font-medium transition"
            >
              {step === "uploading"  ? "Uploading…" :
               step === "extracting" ? "Extracting (this can take a minute)…" :
               step === "done"       ? "Done — redirecting" :
               "Upload & extract"}
            </button>
            <Link to="/ingest" className="text-text-secondary text-sm">Cancel</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
